import React, { useState, useEffect } from 'react';
import { db, auth } from '../../../firebase'; // Asegúrate de que el path a tu config de Firebase sea correcto
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, query, where } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';

const categorias = ['Personal', 'Tareas', 'Pendiente'];

const MisNotas = ({ isOpen, onClose }) => {
  const [user, setUser] = useState(null);
  const [notas, setNotas] = useState([]);
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [nuevaNota, setNuevaNota] = useState('');
  const [nuevaCategoria, setNuevaCategoria] = useState(categorias[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editandoNota, setEditandoNota] = useState(null);
  const [textoEditado, setTextoEditado] = useState('');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser && isOpen) {
        fetchNotas(currentUser.uid);
      }
    });
    return unsubscribe;
  }, [isOpen]);

  const fetchNotas = async (userId) => {
    setLoading(true);
    try {
      const q = query(collection(db, 'MisNotasUsers'), where('userId', '==', userId));
      const querySnapshot = await getDocs(q);
      const notasData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setNotas(notasData.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
    } catch (err) {
      setError('Error al cargar notas: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAgregarNota = async () => {
    if (!user) {
      setError('Debes estar autenticado para agregar notas.');
      return;
    }
    if (!nuevaNota.trim()) {
      setError('La nota no puede estar vacía.');
      return;
    }
    setLoading(true);
    try {
      await addDoc(collection(db, 'MisNotasUsers'), {
        content: nuevaNota,
        category: nuevaCategoria,
        userId: user.uid,
        userName: user.displayName || user.email,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      setNuevaNota('');
      setNuevaCategoria(categorias[0]);
      setError('');
      fetchNotas(user.uid);
    } catch (err) {
      setError('Error al agregar nota: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditarNota = async (nota) => {
    if (!textoEditado.trim()) {
      setError('La nota editada no puede estar vacía.');
      return;
    }
    setLoading(true);
    try {
      const notaRef = doc(db, 'MisNotasUsers', nota.id);
      await updateDoc(notaRef, {
        content: textoEditado,
        category: nuevaCategoria,
        updatedAt: new Date().toISOString(),
      });
      setEditandoNota(null);
      setTextoEditado('');
      setNuevaCategoria(categorias[0]);
      setError('');
      fetchNotas(user.uid);
    } catch (err) {
      setError('Error al editar nota: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEliminarNota = async (notaId) => {
    setLoading(true);
    try {
      const notaRef = doc(db, 'MisNotasUsers', notaId);
      await deleteDoc(notaRef);
      fetchNotas(user.uid);
    } catch (err) {
      setError('Error al eliminar nota: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const notasFiltradas = notas.filter(nota => 
    !filtroCategoria || nota.category === filtroCategoria
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gradient-to-b from-black/70 to-black/50 flex items-center justify-center z-50 transition-opacity duration-500 ease-in-out">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-sm sm:max-w-lg md:max-w-2xl lg:max-w-4xl max-h-[85vh] overflow-y-auto transform transition-all duration-500 ease-in-out scale-95 sm:scale-100 opacity-100 p-0 animate-slide-up">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center sticky top-0 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white bg-clip-text text-transparent bg-gradient-to-r from-yellow-500 to-orange-500">
            Mis Notas
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-white transition duration-300 text-2xl font-bold hover:scale-110 transform"
          >
            ✕
          </button>
        </div>
        <div className="p-6">
          {/* Sección para agregar/editar nota */}
          <div className="mb-6 bg-gray-50 dark:bg-gray-800 p-4 rounded-xl shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              {editandoNota ? 'Editar Nota' : 'Nueva Nota'}
            </h3>
            <textarea
              placeholder="Escribe tu nota aquí..."
              value={editandoNota ? textoEditado : nuevaNota}
              onChange={(e) => editandoNota ? setTextoEditado(e.target.value) : setNuevaNota(e.target.value)}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-4 focus:ring-yellow-300 dark:focus:ring-yellow-500 transition duration-300 resize-y min-h-[100px]"
            />
            <select
              value={nuevaCategoria}
              onChange={(e) => setNuevaCategoria(e.target.value)}
              className="w-full mt-3 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-4 focus:ring-yellow-300 dark:focus:ring-yellow-500 transition duration-300"
            >
              {categorias.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            {error && <p className="text-red-500 mt-2 text-sm">{error}</p>}
            <div className="flex gap-3 mt-4">
              <button
                onClick={editandoNota ? () => handleEditarNota(editandoNota) : handleAgregarNota}
                disabled={loading || (!nuevaNota.trim() && !editandoNota)}
                className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-500 text-white py-3 rounded-lg shadow-md hover:shadow-lg hover:from-yellow-600 hover:to-orange-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all duration-300 font-semibold"
              >
                {loading ? 'Guardando...' : editandoNota ? 'Guardar Cambios' : 'Agregar Nota'}
              </button>
              {editandoNota && (
                <button
                  onClick={() => {
                    setEditandoNota(null);
                    setTextoEditado('');
                    setNuevaCategoria(categorias[0]);
                    setError('');
                  }}
                  className="flex-1 bg-gray-300 dark:bg-gray-600 text-gray-900 dark:text-white py-3 rounded-lg shadow-md hover:bg-gray-400 dark:hover:bg-gray-500 transition duration-300"
                >
                  Cancelar
                </button>
              )}
            </div>
          </div>

          {/* Filtro de categorías */}
          <div className="mb-6">
            <select
              value={filtroCategoria}
              onChange={(e) => setFiltroCategoria(e.target.value)}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-4 focus:ring-yellow-300 dark:focus:ring-yellow-500 transition duration-300"
            >
              <option value="">Todas las Categorías</option>
              {categorias.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {/* Lista de notas */}
          {loading ? (
            <div className="flex justify-center items-center h-40">
              <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-yellow-500"></div>
            </div>
          ) : (
            <ul className="space-y-4 max-h-[50vh] overflow-y-auto">
              {notasFiltradas.map(nota => (
                <li
                  key={nota.id}
                  className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 rounded-xl shadow-sm hover:shadow-md transition-all duration-300"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-gray-900 dark:text-white">{nota.content}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        <strong>Categoría:</strong> {nota.category}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        <strong>Creado:</strong> {new Date(nota.createdAt).toLocaleString('es-ES', { dateStyle: 'medium', timeStyle: 'short' })}
                      </p>
                      {nota.createdAt !== nota.updatedAt && (
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          <strong>Editado:</strong> {new Date(nota.updatedAt).toLocaleString('es-ES', { dateStyle: 'medium', timeStyle: 'short' })}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setEditandoNota(nota);
                          setTextoEditado(nota.content);
                          setNuevaCategoria(nota.category);
                        }}
                        className="text-blue-500 dark:text-blue-400 hover:text-blue-600 dark:hover:text-blue-300 transition duration-200"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleEliminarNota(nota.id)}
                        className="text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 transition duration-200"
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                </li>
              ))}
              {notasFiltradas.length === 0 && (
                <p className="text-center text-gray-600 dark:text-gray-400 py-8 animate-pulse">
                  No hay notas en esta categoría.
                </p>
              )}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default MisNotas;
